import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, DrawerStepper } from '@mercai/clever'

import { useValidationErrors } from '@/helpers'

import { useAiReactiovationMetaTemplateCreate } from '@/services'

import {
  MetaTemplateStep,
  KaiConfigs,
  SearchClientsStep,
  ResultsStep,
} from './components'
import { schemaSelectedLeads } from './drawer-new.constants'

import { ConversationDrawerNewProps } from './drawer-new.type'
import { ProfileDataProps } from './components/search-clients-step/search-clients-step.type'

import {
  IAReactivationMetaTemplateProps,
  IAReactivationMetaTemplateCreateResponseProps,
} from '@/types'

export const ConversationDrawerNew = ({
  isOpen,
  onHandleOpen,
}: ConversationDrawerNewProps) => {
  const { t } = useTranslation('screenAiCenter')
  const [selectedLeads, setSelectedLeads] = useState<ProfileDataProps[]>([])
  const [selectedMetaTemplate, setSelectedMetaTemplate] =
    useState<IAReactivationMetaTemplateProps>()
  const [variableMapping, setVariableMapping] = useState<
    Record<string, string>
  >({})

  const [selectedAgentSlug, setSelectedAgentSlug] = useState<string>('')
  const [selectedTagsSlugs, setSelectedTagsSlugs] = useState<string[]>([])
  const [resultsData, setResultsData] =
    useState<IAReactivationMetaTemplateCreateResponseProps>()

  const availableVariables = Object.keys(selectedLeads?.[0] || {}) || []

  const {
    errors: errorsSelectedLeads,
    onHandleListenErrors: onHandleListenErrorsSelectedLeads,
    isValid: isValidSelectedLeads,
  } = useValidationErrors(schemaSelectedLeads(t), {
    selectedLeads,
  })

  const {
    mutateAsync: mutateCreateReactivationMetaTemplate,
    isPending: isPendingCreateReactivationMetaTemplate,
  } = useAiReactiovationMetaTemplateCreate()

  const onSubmitSelectedLeads = ({
    onNavigateToNextStep,
  }: {
    onNavigateToNextStep: (route: string) => void
  }) => {
    if (!isValidSelectedLeads) {
      onHandleListenErrorsSelectedLeads(true)

      return
    }

    onHandleListenErrorsSelectedLeads(false)

    onNavigateToNextStep('metaTemplateList')
  }

  const onSubmitMetaTemplate = ({
    onNavigateToNextStep,
  }: {
    onNavigateToNextStep: (route: string) => void
  }) => {
    onNavigateToNextStep('kaiConfigs')
  }

  const onHandleSubmit = async ({
    onNavigateToNextStep,
  }: {
    onNavigateToNextStep: (route: string) => void
  }) => {
    const response = await mutateCreateReactivationMetaTemplate({
      templateId: selectedMetaTemplate?.id || '',
      contacts: selectedLeads as [],
      variableMapping: variableMapping as Record<string, string>,
      agentSlug: selectedAgentSlug,
      conversationTagsSlugs: selectedTagsSlugs,
    })

    if (response?.data) {
      setResultsData(response.data)
      onNavigateToNextStep('results')
    }
  }

  return (
    <DrawerStepper
      initialStep="root"
      isOpen={isOpen}
      onHandleOpen={onHandleOpen}
      title={t('conversationNewHeaderTitle')}
      translations={{
        optional: t('conversationNewOptional'),
      }}
      steps={{
        root: {
          component: (
            <SearchClientsStep
              onSelectedLeads={setSelectedLeads}
              selectedLeads={selectedLeads}
              errors={errorsSelectedLeads as Record<string, string>}
            />
          ),
          footer: ({ onNavigateToNextStep }) => (
            <>
              <Button variant="secondary" onClick={() => onHandleOpen()}>
                {t('conversationNewNavigateButtonBack')}
              </Button>

              <Button
                onClick={() => onSubmitSelectedLeads({ onNavigateToNextStep })}
              >
                {t('conversationNewNavigateButtonNext')}
              </Button>
            </>
          ),
          backNavigate: 'root',
          isOptional: false,
        },
        metaTemplateList: {
          component: (
            <MetaTemplateStep
              onSelectMetaTemplate={setSelectedMetaTemplate}
              selectedMetaTemplate={
                selectedMetaTemplate as IAReactivationMetaTemplateProps
              }
              availableVariables={availableVariables}
              onMappingChange={setVariableMapping}
            />
          ),
          backNavigate: 'root',
          isOptional: false,
          footer: ({ onNavigateToNextStep, onNavigateToPreviousStep }) => (
            <>
              <Button
                variant="secondary"
                onClick={() => onNavigateToPreviousStep()}
              >
                {t('conversationNewNavigateButtonBack')}
              </Button>

              <Button
                onClick={() => onSubmitMetaTemplate({ onNavigateToNextStep })}
              >
                {t('conversationNewNavigateButtonNext')}
              </Button>
            </>
          ),
        },
        kaiConfigs: {
          component: (
            <KaiConfigs
              onHandleSelectAgent={setSelectedAgentSlug}
              onHandleSelectTags={setSelectedTagsSlugs}
              selectedAgent={selectedAgentSlug}
              selectedTags={selectedTagsSlugs}
            />
          ),
          backNavigate: 'metaTemplateList',
          isOptional: false,
          footer: ({ onNavigateToPreviousStep, onNavigateToNextStep }) => (
            <>
              <Button
                variant="secondary"
                onClick={() => onNavigateToPreviousStep()}
              >
                {t('conversationNewNavigateButtonBack')}
              </Button>

              <Button
                onClick={() => onHandleSubmit({ onNavigateToNextStep })}
                isLoading={isPendingCreateReactivationMetaTemplate}
              >
                {t('conversationNewNavigateButtonSubmit')}
              </Button>
            </>
          ),
        },
        results: {
          component: <ResultsStep data={resultsData} />,
          backNavigate: 'kaiConfigs',
          isOptional: false,
          footer: () => (
            <>
              <Button variant="primary" onClick={() => onHandleOpen()}>
                {t('conversationNewNavigateButtonClose')}
              </Button>
            </>
          ),
        },
      }}
    />
  )
}
